import { Link } from "@inertiajs/react"
import { ChevronRight, Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StreakWeekStrip } from "@/components/creator/streak/streak-week-strip"
import { usePreviewMode } from "@/components/profile-preview/preview-mode"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { creatorStreakPath } from "@/routes"
import type { ProfileStreakSummary } from "@/types/streak"

/**
 * OKS-517 — §11 streak card (card 3) → entry into the §12 streak detail.
 *
 * The shipped OKS-513 card was a non-navigating day-count (KD-8 deferred
 * the destination here). This rewrite (a) makes the WHOLE card navigate to
 * `/streak` via `creatorStreakPath()`, and (b) fleshes out the 3 Figma
 * states, the this-week dots, personal best, and progress to the next
 * streak badge — all from the additive nested `streak` summary
 * (`Streaks::SummaryProps`); the client computes no streak math.
 *
 * # Card states (TRD KD-3 — classified on current/longest, not the page
 * dormant flag; `data-state` replaces OKS-513's boolean `data-broken`)
 *   - active  — `current_streak_days >= 1` (covers the resolver's
 *               active / at_risk / frozen — a frozen run is preserved, so
 *               it reads as active per PRD §3). Shows the enriched subtitle
 *               + week strip + "Keep it alive →".
 *   - empty   — `current == 0 && longest == 0` (never posted).
 *   - dormant — `current == 0 && longest >= 1` (a prior run reset).
 *
 * # Navigation (OKS-400 preview posture)
 * On a creator's own profile the whole card is wrapped in an Inertia
 * `<Link>` (the entire card is the tap target). In a brand-admin read-only
 * preview it renders inert (no `<a>`, `aria-disabled`, no link role) —
 * consistent with every other preview affordance.
 *
 * # Defensive (UX §3)
 * A null/absent `streak` degrades to the empty state rather than erroring.
 *
 * Meaning is carried by TEXT (title + subtitle), never the flame colour
 * alone (UX Spec §6).
 */
interface ProfileStreakCardProps {
  streak: ProfileStreakSummary | null | undefined
}

type CardState = "active" | "empty" | "dormant"

function classify(streak: ProfileStreakSummary | null | undefined): CardState {
  if (!streak || streak.current_streak_days < 1) {
    if (streak && streak.longest_streak_days >= 1) return "dormant"
    return "empty"
  }
  return "active"
}

export function ProfileStreakCard({ streak }: ProfileStreakCardProps) {
  const { t } = useTranslation()
  const preview = usePreviewMode()
  const state = classify(streak)
  const active = state === "active"

  const title = active
    ? t("profile.streak.title", { value: streak!.current_streak_days })
    : t(`profile.streak.${state}.title`)

  // Active subtitle: "Personal best {M}" + (when the badge isn't earned yet)
  // " · {K} days to {badge}". The clause is dropped once next_streak_badge
  // is null (PRD §3). Non-active states use their own one-line supporting copy.
  const badge = streak?.next_streak_badge
  const personalBest = t("profile.streak.subtitle.best", {
    value: streak?.longest_streak_days ?? 0,
  })
  const toBadge =
    badge != null
      ? t("profile.streak.subtitle.to_badge", {
          count: badge.remaining_days,
          badge: t(`badges.milestones.${badge.i18n_key}.name`),
        })
      : null
  const subtitle = active
    ? [personalBest, toBadge].filter(Boolean).join(" · ")
    : t(`profile.streak.${state}.supporting`)

  const ariaLabel = t("profile.streak.aria", { title, subtitle })

  const card = (
    <Card data-testid="profile-streak-card" data-state={state}>
      <CardContent className="flex flex-col gap-3 py-4">
        <div className="flex items-center gap-4">
          <Flame
            aria-hidden="true"
            className={cn(
              "size-7 shrink-0",
              active ? "text-orange-500" : "text-muted-foreground",
            )}
          />
          <div className="flex min-w-0 flex-col">
            <span
              data-testid="profile-streak-title"
              className={cn(
                "text-base font-semibold",
                !active && "text-muted-foreground",
              )}
            >
              {title}
            </span>
            <span className="text-muted-foreground truncate text-sm">
              {subtitle}
            </span>
          </div>
          <ChevronRight
            aria-hidden="true"
            className="text-muted-foreground ml-auto size-5 shrink-0"
          />
        </div>

        {active && (
          <>
            <StreakWeekStrip days={streak!.this_week} />
            {!preview && (
              <span
                data-testid="profile-streak-keep-alive"
                aria-hidden="true"
                className="text-primary self-end text-sm font-medium"
              >
                {t("profile.streak.keep_alive")}
              </span>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )

  // Read-only preview: inert, no link role, no navigation (OKS-400).
  if (preview) {
    return <div aria-disabled="true">{card}</div>
  }

  // Own profile: the whole card is the tap target.
  return (
    <Link
      href={creatorStreakPath()}
      aria-label={ariaLabel}
      className="focus-visible:ring-ring block rounded-xl transition-colors hover:opacity-95 focus-visible:ring-2 focus-visible:outline-none"
    >
      {card}
    </Link>
  )
}
